import { EmployeeTrainingTable } from '@/components/compliance/people/EmployeeTrainingTable'
import { EmployeePoliciesTable } from '@/components/compliance/people/EmployeeComplianceSections'
import { BACKGROUND_CHECK_LABEL, EmployeeTypeBadge } from '@/components/compliance/people/EmployeeDirectory'
import { useEffect, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useParams } from '@tanstack/react-router'
import { Bell, Loader2, Pencil } from 'lucide-react'
import { format } from 'date-fns'
import { toast } from 'sonner'
import { trpc } from '@/lib/trpc'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import MainHeader from '@/components/layout/MainHeader'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { Button } from '@deepguide-ai/ui/button'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@deepguide-ai/ui/tabs'
import { EmployeeBackgroundCheckSection } from '@/components/compliance/people/EmployeeBackgroundCheckSection'
import { Skeleton } from '@deepguide-ai/ui/skeleton'
import { StatCard } from '@deepguide-ai/ui/stat-card'
import { StatCardSkeleton } from '@deepguide-ai/ui/stat-card-skeleton'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@deepguide-ai/ui/breadcrumb'
import {
  DetailSection,
  DetailStatusGrid,
  DetailStatusItem,
} from '@/components/compliance/detail-primitives'
import { MemberAvatar } from '@/components/compliance/MemberAvatar'
import { EditEmployeeDialog } from '@/components/compliance/people/EditEmployeeDialog'
import { EmployeeStatusBadge } from '@/components/compliance/people/EmployeeStatusBadge'
import { LifecycleStatusBadge } from '@/components/compliance/people/LifecycleStatusBadge'
import { DevicesSection } from '@/components/compliance/people/DevicesSection'
import { PersonAssetRelationships } from '@/components/compliance/PersonAssetRelationships'
import type { EmployeeDeviceDTO, TrainingRecordDTO } from '@deepguide-ai/types'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/people_/$employeeId'
)({
  component: EmployeeDetailPage,
  validateSearch: (search: { tab?: unknown }) => ({
    tab: ['devices', 'training', 'policies', 'assets', 'background'].includes(String(search.tab))
      ? search.tab as 'devices' | 'training' | 'policies' | 'assets' | 'background'
      : 'devices' as const,
  }),
  staticData: {
    pageHeader: { title: 'People', customHeader: true, pad: false },
  },
})


function formatDate(value: Date | string | null | undefined, withTime = false) {
  if (!value) return null
  return format(new Date(value), withTime ? 'MMM d, yyyy h:mm a' : 'MMM d, yyyy')
}

function EmployeeDetailSkeleton() {
  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden">
      <MainHeader className="!mb-0 !h-[50px] p-0 pl-4">
        <div className="flex items-center gap-2 px-1">
          <Skeleton className="h-4 w-14 rounded-sm" />
          <Skeleton className="h-3 w-3 rounded-sm" />
          <Skeleton className="h-4 w-24 rounded-sm" />
        </div>
      </MainHeader>

      <ScrollArea className="min-h-0 flex-1">
        <div className="mx-auto w-full max-w-5xl space-y-8 px-6 py-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <Skeleton className="h-12 w-12 shrink-0 rounded-full" />
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-7 w-36 rounded-sm" />
                  <Skeleton className="h-5 w-14 rounded-sm" />
                </div>
                <Skeleton className="h-4 w-48 rounded-sm" />
                <Skeleton className="h-3 w-24 rounded-sm" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-8 w-16 rounded-sm" />
              <Skeleton className="h-8 w-28 rounded-sm" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <StatCardSkeleton
                key={index}
                variant="controls"
                className="min-h-[90px]"
              />
            ))}
          </div>

          <section className="space-y-5">
            <Skeleton className="h-3 w-14 rounded-sm" />
            <div className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="space-y-2">
                  <Skeleton className="h-3 w-24 rounded-sm" />
                  <Skeleton className="h-4 w-32 rounded-sm" />
                </div>
              ))}
            </div>
          </section>

          <section className="space-y-4">
            <Skeleton className="h-3 w-20 rounded-sm" />
            <Skeleton className="h-4 w-64 max-w-full rounded-sm" />
          </section>

          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <Skeleton className="h-3 w-24 rounded-sm" />
              <Skeleton className="h-8 w-28 rounded-sm" />
            </div>
            <Skeleton className="h-32 w-full rounded-md" />
          </section>
        </div>
      </ScrollArea>
    </div>
  )
}

function EmployeeDetailPage() {
  const { workspaceId, employeeId } = useParams({ strict: false }) as {
    workspaceId: string
    employeeId: string
  }
  const queryClient = useQueryClient()
  const { canWorkspaceWrite, isAdmin } =
    useWorkspacePermissions(workspaceId)
  const [editOpen, setEditOpen] = useState(false)
  const { tab } = Route.useSearch()
  const navigate = Route.useNavigate()
  const backgroundSectionRef = useRef<HTMLDivElement>(null)

  const dashboardQuery = useQuery(
    trpc.employee.getComplianceDashboard.queryOptions({ employeeId })
  )

  const backgroundQuery = useQuery({
    ...trpc.backgroundCheck.getForEmployee.queryOptions({ employeeId }),
    enabled: isAdmin,
  })

  useEffect(() => {
    if (isAdmin && tab === 'background') {
      backgroundSectionRef.current?.scrollIntoView({ block: 'start' })
    }
  }, [isAdmin, tab, dashboardQuery.data?.employee.id])

  const invalidateDashboard = () => {
    queryClient.invalidateQueries({
      queryKey: trpc.employee.getComplianceDashboard.queryKey({ employeeId }),
    })
  }

  const reminderMutation = useMutation({
    ...trpc.employee.sendSelfServiceReminders.mutationOptions(),
    onSuccess: (result) => {
      if (result.emailsSent > 0) {
        toast.success('Reminder email sent')
      } else {
        toast.error(result.failures[0]?.error ?? 'Failed to send reminder email')
      }
      invalidateDashboard()
    },
    onError: (err: Error) => toast.error(err.message ?? 'Failed to send reminder email'),
  })

  if (dashboardQuery.isLoading) return <EmployeeDetailSkeleton />

  const dashboard = dashboardQuery.data
  if (!dashboard) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <p className="text-muted-foreground">Employee not found</p>
      </div>
    )
  }

  const { employee, pendingPolicies, completedAcknowledgments, trainingRecords, devices } =
    dashboard
  const portal = dashboard.selfServiceAccessStatus

  const displayName =
    `${employee.firstName} ${employee.lastName}`.trim() || employee.email.split('@')[0]
  const titleLine = [employee.title, employee.department].filter(Boolean).join(' · ')

  const compliantDevices = devices.filter(
    (device: EmployeeDeviceDTO) => device.complianceStatus === 'COMPLIANT'
  ).length
  // WAIVED = duty waived — excluded from both sides (same rule as the Directory glyphs).
  const assignedTraining = trainingRecords.filter(
    (record: TrainingRecordDTO) => record.status !== 'WAIVED'
  )
  const completedTraining = assignedTraining.filter(
    (record: TrainingRecordDTO) => record.status === 'COMPLETED'
  ).length

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden">
      <MainHeader className="!mb-0 !h-[50px] p-0 pl-4">
        <div className="flex items-center gap-2 px-1">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/workspaces/$workspaceId/people" params={{ workspaceId }}>
                    People
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{displayName}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </MainHeader>

      <ScrollArea className="min-h-0 flex-1">
        <div className="mx-auto w-full max-w-5xl space-y-8 px-6 py-6">
          {/* Identity header */}
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <MemberAvatar
                person={{
                  firstName: employee.firstName,
                  lastName: employee.lastName,
                  email: employee.email,
                }}
                size="lg"
                className="h-12 w-12"
              />
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="font-heading text-[24px] leading-[1.3] font-bold text-text-secondary">
                    {displayName}
                  </h1>
                  <EmployeeStatusBadge status={employee.status} />
                  <LifecycleStatusBadge status={employee.lifecycleStatus} />
                </div>
                <div className="mt-0.5 text-sm text-muted-foreground">{employee.email}</div>
                {titleLine && (
                  <div className="mt-0.5 text-xs text-text-subtle">{titleLine}</div>
                )}
              </div>
            </div>
            {canWorkspaceWrite && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </Button>
                {pendingPolicies.length > 0 && (
                  <Button
                    size="sm"
                    onClick={() => reminderMutation.mutate({ employeeIds: [employee.id] })}
                    disabled={reminderMutation.isPending}
                  >
                    {reminderMutation.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Bell className="h-3.5 w-3.5" />
                    )}
                    Send Reminder
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Posture stat cards */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatCard
              label="Devices Compliant"
              value={devices.length === 0 ? '—' : `${compliantDevices}/${devices.length}`}
            />
            <StatCard
              label="Training Completed"
              value={
                assignedTraining.length === 0
                  ? '—'
                  : `${completedTraining}/${assignedTraining.length}`
              }
            />
            <StatCard label="Pending Acknowledgments" value={pendingPolicies.length} />
            <StatCard
              label="Background Check"
              value={
                <span className="text-[16px]">
                  {BACKGROUND_CHECK_LABEL[backgroundQuery.data?.summary?.backgroundCheckStatus ?? employee.backgroundCheckStatus ?? ''] ??
                    'Not tracked'}
                </span>
              }
            />
          </div>

          {/* Profile & portal */}
          <DetailSection title="Profile">
            <p className="font-body text-[12px] text-muted-foreground">
              Employment details, portal access, and account linkage for this person.
            </p>
            <DetailStatusGrid className="max-w-none sm:grid-cols-3">
              <DetailStatusItem label="Employment Type">
                <span className="text-sm text-text-body">
                  <EmployeeTypeBadge type={employee.type} />
                </span>
              </DetailStatusItem>
              <DetailStatusItem label="Onboard date">
                <span className="text-sm text-text-body">
                  {formatDate(employee.startDate) ?? '—'}
                </span>
              </DetailStatusItem>
              <DetailStatusItem label="User Account">
                <span className="text-sm text-text-body">
                  {employee.userId ? 'Linked' : 'Unlinked'}
                </span>
              </DetailStatusItem>
              <DetailStatusItem label="Portal Access">
                <span className="text-sm text-text-body">
                  {(portal?.hasActiveAccess ?? employee.hasSelfServiceAccess)
                    ? 'Active'
                    : 'Inactive'}
                </span>
              </DetailStatusItem>
              <DetailStatusItem label="Last Portal Activity">
                <span className="text-sm text-text-body">
                  {formatDate(portal?.lastSelfServiceAt ?? employee.lastSelfServiceAt, true) ??
                    'Never'}
                </span>
              </DetailStatusItem>
              <DetailStatusItem label="Last Reminder">
                <span className="text-sm text-text-body">
                  {formatDate(portal?.lastReminderAt, true) ?? 'Never'}
                </span>
              </DetailStatusItem>
            </DetailStatusGrid>
          </DetailSection>

          <div ref={backgroundSectionRef}>
            <Tabs
              className="gap-6"
              value={tab === 'background' && !isAdmin ? 'devices' : tab}
              onValueChange={(value) => navigate({
                search: { tab: value as typeof tab },
                replace: true,
              })}
            >
              <TabsList className="w-full" aria-label="Employee details">
                <TabsTrigger value="devices">Devices</TabsTrigger>
                <TabsTrigger value="training">Training</TabsTrigger>
                <TabsTrigger value="policies">Policies</TabsTrigger>
                <TabsTrigger value="assets">Assets &amp; Access</TabsTrigger>
                {isAdmin && <TabsTrigger value="background">Background</TabsTrigger>}
              </TabsList>
              <TabsContent value="devices">
                <DevicesSection
                  devices={devices}
                  displayStyle="table"
                  allowEvidenceUpload={canWorkspaceWrite}
                />
              </TabsContent>
              <TabsContent value="training">
                <EmployeeTrainingTable records={trainingRecords.map((record) => ({ ...record, name: record.program?.name ?? record.programId }))} />
              </TabsContent>
              <TabsContent value="policies">
                <EmployeePoliciesTable pendingPolicies={pendingPolicies} acknowledgments={completedAcknowledgments} readOnly />
              </TabsContent>
              <TabsContent value="assets">
                <PersonAssetRelationships
                  workspaceId={workspaceId}
                  employeeId={employee.id}
                  readOnly={!canWorkspaceWrite}
                  canManageOwnership={isAdmin}
                />
              </TabsContent>
              {isAdmin && (
                <TabsContent value="background">
                  <EmployeeBackgroundCheckSection
                    key={employee.id}
                    employeeId={employee.id}
                    readOnly={!canWorkspaceWrite}
                  />
                </TabsContent>
              )}
            </Tabs>
          </div>
        </div>
      </ScrollArea>

      <EditEmployeeDialog
        employee={editOpen ? employee : null}
        open={editOpen}
        onOpenChange={(open) => {
          setEditOpen(open)
          if (!open) invalidateDashboard()
        }}
      />
    </div>
  )
}
